import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Heart } from "lucide-react";
import Seo from "../components/Seo";
import { directors } from "../data/directors";

const accentRing = {
  magenta: "ring-[#E91E63]/40 shadow-[0_30px_60px_-20px_rgba(233,30,99,0.45)]",
  wine: "ring-[#A02157]/40 shadow-[0_30px_60px_-20px_rgba(160,33,87,0.45)]",
  gold: "ring-[#FFB72E]/50 shadow-[0_30px_60px_-20px_rgba(255,183,46,0.45)]",
};

const accentBadge = {
  magenta: "bg-brand-primary-light text-brand-primary",
  wine: "bg-[#F7DDE7] text-brand-wine",
  gold: "bg-brand-gold-light text-[#7A4E00]",
};

export default function Team() {
  return (
    <div className="relative overflow-hidden" data-testid="team-page">
      <Seo
        title="Our Leadership — Dkarez Community Support CIC"
        description="Meet the Founder and Directors guiding Dkarez Community Support CIC — leaders committed to dignity, safeguarding and opportunity for vulnerable children."
      />

      {/* Ambient brand glows */}
      <div className="gold-glow bg-brand-primary-light w-[520px] h-[520px] -top-40 -left-40" />
      <div className="gold-glow bg-brand-gold-light w-[480px] h-[480px] -bottom-40 -right-32" />

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-20 sm:pt-24 pb-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <span className="eyebrow" data-testid="team-eyebrow">Our Leadership</span>
          <h1 className="font-display-bold text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[1.02]">
            The hands &amp; hearts behind{" "}
            <span className="font-script text-brand-primary text-[1.25em] align-[-0.05em]">
              Dkarez
            </span>
          </h1>
          <p className="mt-7 text-lg text-brand-muted max-w-2xl leading-relaxed">
            Four directors. One conviction — that every vulnerable child deserves dignity,
            stability and the opportunity to reach their full potential.
          </p>
        </motion.div>
      </section>

      {/* Director grid */}
      <section className="relative max-w-6xl mx-auto px-4 sm:px-8 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-7 sm:gap-10">
          {directors.map((d, i) => (
            <motion.div
              key={d.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
            >
              <Link
                to={`/team/${d.slug}`}
                data-testid={`director-card-${d.slug}`}
                className="group block"
              >
                <div className="glass-card depth-shadow rounded-[28px] overflow-hidden transition-all duration-500 hover:-translate-y-1.5">
                  {/* Portrait — 6D framed */}
                  <div className="relative">
                    <div className="aspect-[4/5] overflow-hidden">
                      <img
                        src={d.photo}
                        alt={d.name}
                        loading="lazy"
                        className={`w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04] ring-1 ring-inset ${accentRing[d.accent]}`}
                      />
                    </div>
                    {/* Floating script accent */}
                    <span className="absolute top-4 left-5 font-script text-3xl text-white drop-shadow-[0_4px_14px_rgba(160,33,87,0.6)]">
                      0{i + 1}
                    </span>
                    {/* Bottom magenta veil */}
                    <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[rgba(42,14,31,0.55)] to-transparent" />
                  </div>

                  {/* Copy */}
                  <div className="p-6 sm:p-7">
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-[0.7rem] font-semibold tracking-wider uppercase ${accentBadge[d.accent]}`}
                    >
                      {d.title}
                    </span>
                    <h3 className="font-display-bold text-2xl sm:text-[1.6rem] mt-3 text-brand-text leading-tight">
                      {d.name}
                    </h3>
                    <p className="mt-3 text-brand-muted leading-relaxed text-[0.95rem]">
                      {d.short}
                    </p>

                    <div className="mt-5 flex items-center gap-2 text-brand-primary font-medium text-sm group-hover:gap-3 transition-all">
                      Read full profile
                      <ArrowUpRight
                        size={16}
                        strokeWidth={2}
                        className="transition-transform group-hover:rotate-45"
                      />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative max-w-5xl mx-auto px-4 sm:px-8 pb-28">
        <div className="premium-card depth-shadow-magenta p-10 sm:p-14 text-center relative overflow-hidden">
          <Sparkles className="mx-auto text-brand-gold" size={28} strokeWidth={1.4} />
          <h2 className="font-display-bold text-4xl sm:text-5xl mt-4 leading-tight">
            Stand with our directors.
          </h2>
          <p className="mt-4 text-brand-muted max-w-2xl mx-auto leading-relaxed">
            Their vision is only possible because of supporters like you. One small act today
            becomes a lifetime of dignity for a child tomorrow.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/campaigns" className="btn-gold" data-testid="team-cta-donate">
              <Heart size={16} className="mr-2" strokeWidth={1.8} fill="currentColor" />
              Donate Now
            </Link>
            <Link to="/about" className="btn-outline-gold" data-testid="team-cta-about">
              About our mission
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
